import React from "react";

import "./Blog.css";
import NavLight from "../../components/NavLight";
import BlogPageIntroImg from "../../assets/blog/blog_Vanessa_Desi_Damblemont.png";
import BlogsPreview from "../../components/BlogsPreview";
import FoundersGuide from "../../components/FoundersGuide";
import {CustomMetaHelmet} from "../../components/CustomMetaHelmet";
import {meta} from "../../assets/meta/meta";

function HowToBuildBetterProductByCreatingAStrongStory() {
  return (
    <div className="Blog1">
      <CustomMetaHelmet meta={meta['how-to-build-better-products-by-creating-a-strong-story']}/>
      <NavLight />
      <section className="blog-page">
        <article className="blog-page-container">
          <div>
            <h1>How to build better products by creating a strong story</h1>
            <p>
              <i>
                In this thought leadership article, I'm speaking with senior
                product manager, Vanessa Desi Damblemont, about how product
                managers can build better products by crafting a strong product
                story. Vanessa has spent years managing products for everything
                from SMBs to large enterprise customers, and in this article she
                is sharing her best practices and advice for product managers
                who want to improve their decision-making process.
              </i>
            </p>
          </div>
          <div>
            <figure>
              <img
                src={BlogPageIntroImg}
                alt="purple hand pressing a digital button"
              />
            </figure>
          </div>
          <div>
            <p>
              One of the challenges many product managers face when involving
              customers in their decision-making process is that their product
              might have many companies using it and within those companies many
              users.
            </p>

            <p>&nbsp;</p>

            <p>
              When it comes to involving users and customers in the
              decision-making process, Vanessa recommends balancing different
              feedback sources to get a more comprehensive understanding.
              <p>&nbsp;</p>
              <q>When you have many customers and users using your product,
              you want to avoid getting biased by only focusing on a few
              customer interviews. I always try to balance feedback from
              different sources and customers. I go through feedback provided
              through our feedback buttons, tickets raised to support, notes
              from customer success managers, and conduct customer
              interviews.</q>
            </p>

            <p>&nbsp;</p>

            <p>
              But when do you have enough customer feedback to make a product
              decision? According to Vanessa, you have to collect feedback until
              you start seeing a key direction to solve a problem:
              <p>&nbsp;</p>
              <q>I collect feedback, bottom-up, until I have enough evidence
              to see a key direction for solving a problem. Then, I try to match
              the key direction with everything I know to make sure I haven't
              forgotten anything. Afterwards, I will refine and iterate on the
              solution, then craft a short and clear story about what it
              does.</q>
            </p>

            <p>&nbsp;</p>

            <p>
              And there's a really good reason behind why Vanessa starts
              building the story around the solution early in the process:
              <q>Creating a story around what you are building gives you a
              long-term direction and helps the people you're interacting with
              get more connected to the vision. It gives a common view of where
              you're going and provides something people can easily buy
              into.</q>
            </p>

            <p>&nbsp;</p>

            <p>
              According to Vanessa, it's important to craft a story that will
              make people excited to follow you. But as she also mentions, you
              need to do your homework and never start by presenting a blank
              page:
              <p>&nbsp;</p>
              <q>Creating a story is an interactive and collaborative
              process. But I had a manager a long time ago that gave me great
              advice: 'Never start from a blank page, always have something to
              showcase.' So make sure you always have something to start off
              with. Just start, do something to craft the narrative. Look at
              your data, do some quick user interviews. So I really try to apply
              that method of working. In France, we have a saying that says,
              'Say something wrong and you get to the truth.' Because if you say
              the wrong things and talk with enough smart people, then at some
              point you get to the truth.</q>
            </p>

            <p>&nbsp;</p>

            <p>
              However, this approach of seeking the truth is not only helpful in
              building a strong product story, it also helps drive awareness and
              decision-making in the process.
              <p>&nbsp;</p>
              <q>When you involve people in the process, you build a certain
              level of confidence in what you have discovered. And by
              interacting with the right stakeholders and creating visibility,
              they will also be able to tell you if they believe you are wrong
              or going in the wrong direction early on, instead of too
              late.</q>
            </p>

            <p>&nbsp;</p>

            <p>
              In this pursuit of not starting from a blank page and getting to
              the truth, one thing to keep in mind is the influence of your own
              intuition as a product manager. Vanessa believes product managers
              need to be aware that their intuition is always part of the
              process.
              <p>&nbsp;</p>
              <q>You always make a decision about how much data and what
              kind of effort you put into understanding a specific problem. In
              fact, even before you get the data, you already have an idea in
              mind. Maybe the data will change your mind, or maybe it will help
              you get a better understanding. So what's important is to have an
              iterative mindset. When you have no data, what do you think about
              this problem and why? And then when you have the data, how does it
              change your thinking and why? At some point, your views on the
              problem get stronger and stronger and help you create a strong
              story.</q>
            </p>

            <p>&nbsp;</p>

            <p>
              But what do you do when you craft a strong story and stakeholders
              disagree with the solution you come up with? How do you handle
              that disagreement?
              <p>&nbsp;</p>
              <q>It depends. But generally, it is important to always have a
              mindset of being as objective as possible and do your best to
              understand the rationale behind the disagreement. Be open minded
              to the fact that sometimes you can be wrong. Always remember that
              it is your job to give as much clarity and information as needed
              to make the right decision. And more importantly, always remind
              stakeholders that not making a decision is in fact a decision by
              itself, and that not making a decision can have real consequences
              for our customers, users and business.</q>
            </p>

            <p>&nbsp;</p>

            <p>
              While many bottlenecks on decision making can often be resolved in
              a timely manner, sometimes situations may arrive that are hard to
              unblock. For those situations Vanessa recommends thinking beyond
              the decision at hand:
              <p>&nbsp;</p>
              <q>The biggest problem is when people completely disagree with
              each other and are misaligned. There can be different things that
              are at stake at different levels. It's not always about the
              solution. Sometimes it’s something unresolved in the company
              strategy and then you need to escalate to people involved in more
              high-level discussions. </q>
            </p>

            <p>&nbsp;</p>

            <p>
              What's one piece of advice you would like to give to other product
              managers and aspiring product managers?
              <p>&nbsp;</p>
              <q>It's important that you like people. You will spend so much
              time interacting with people in groups, individually, and so on,
              and you will never be perfect at dealing with people. So it's
              important to genuinely like people, get to know them well, and
              appreciate that everyone has a role to play. Never forget to be
              nice to people. This job simply has too much people interaction to
              only focus on figures and documents. Embrace the people and
              everything else will become easier in the role.</q>
            </p>
          </div>
        </article>
      </section>
      <section className="related-blogs">
        <h1> You might also like</h1>
        <BlogsPreview />
      </section>
      <section className="FoundersGuide">
        <FoundersGuide />
      </section>
    </div>
  );
}

export default HowToBuildBetterProductByCreatingAStrongStory;
